// frontend/src/components/ColdStartLoader.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, CircularProgress, Button } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { checkBackendHealth } from '../services/healthService';

/**
 * ColdStartLoader - A branded loading component that displays during backend cold starts.
 *
 * Shows the BidTransparency logo with a spinner while the backend warms up,
 * and an error state if the server fails to respond.
 *
 * @param {React.ReactNode} children - The content to render once backend is ready
 * @param {number} timeoutMs - Time before showing error state (default: 30000ms)
 */
function ColdStartLoader({ children, timeoutMs = 30000 }) {
    const [isReady, setIsReady] = useState(false);
    const [isError, setIsError] = useState(false);

    // Use refs to track intervals and timeouts for cleanup
    const pollingIntervalRef = useRef(null);
    const errorTimeoutRef = useRef(null);

    // Cleanup function to clear all timers
    const cleanup = useCallback(() => {
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }
        if (errorTimeoutRef.current) {
            clearTimeout(errorTimeoutRef.current);
            errorTimeoutRef.current = null;
        }
    }, []);

    // Start the health check polling
    const startHealthCheck = useCallback(() => {
        // Reset state
        setIsReady(false);
        setIsError(false);

        // Clean up any existing timers
        cleanup();

        // Immediately check health
        const checkHealth = async () => {
            const healthy = await checkBackendHealth();
            if (healthy) {
                cleanup();
                setIsReady(true);
            }
        };

        // Initial check
        checkHealth();

        // Poll every 2 seconds
        pollingIntervalRef.current = setInterval(checkHealth, 2000);

        // Set error timeout
        errorTimeoutRef.current = setTimeout(() => {
            cleanup();
            setIsError(true);
        }, timeoutMs);
    }, [cleanup, timeoutMs]);

    // Start health check on mount
    useEffect(() => {
        startHealthCheck();

        // Cleanup on unmount
        return cleanup;
    }, [startHealthCheck, cleanup]);

    // Handle retry button click
    const handleRetry = () => {
        startHealthCheck();
    };

    // If backend is ready, render children
    if (isReady) {
        return <>{children}</>;
    }

    // Render loading or error state
    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#fff',
                padding: 3,
            }}
        >
            {/* Logo */}
            <Box
                sx={{
                    mb: 4,
                    width: '100%',
                    maxWidth: '250px',
                    display: 'flex',
                    justifyContent: 'center',
                }}
            >
                <img
                    src="/logo.png"
                    alt="BidTransparency Logo"
                    style={{
                        width: '100%',
                        height: 'auto',
                    }}
                />
            </Box>

            {isError ? (
                // Error state
                <>
                    <ErrorOutlineIcon
                        sx={{
                            fontSize: 48,
                            color: 'error.main',
                            mb: 2,
                        }}
                    />
                    <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ mb: 3, textAlign: 'center' }}
                    >
                        Unable to connect to server
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleRetry}
                        sx={{ py: 1.5, px: 4 }}
                    >
                        Retry
                    </Button>
                </>
            ) : (
                // Loading state
                <>
                    <CircularProgress
                        size={40}
                        sx={{ mb: 3 }}
                    />
                    <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ textAlign: 'center' }}
                    >
                        Loading...
                    </Typography>
                </>
            )}
        </Box>
    );
}

export default ColdStartLoader;
